import { builder } from "@/builders";
import { makePath, PAGES } from "@idss/libraries";
import { handleError, handleSuccess } from "@idss/notification";
import {
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Form } from "@mantine/form";
import { useMutation } from "@tanstack/react-query";
import { useFormContext } from "./password-reset-form-context";

import Link from "next/link";

interface RequestOTPProps {
  unique_link?: string;
}

export function RequestOTP({ unique_link }: RequestOTPProps) {
  const form = useFormContext();

  const { mutate, isPending } = useMutation({
    mutationFn: builder.$use.auth.password.forgot,
    onError: handleError(),
    onSuccess: ({ data }) => {
      handleSuccess({
        message: data.message,
      });

      form.setFieldValue("reset_password", true);
    },
  });

  function handleSubmit({ email }: typeof form.values) {
    mutate({ email });
  }

  return (
    <Stack gap={32}>
      <Stack gap={8} className="text-primary-text-body">
        <h2 className="prose-2xl/medium">Forgot Password</h2>
        <p className="prose-base/regular">
          We&apos;ll send you an email so you can reset your password
        </p>
      </Stack>

      <Box component={Form} form={form} onSubmit={handleSubmit} w="100%">
        <Stack gap={16}>
          <TextInput
            label="Email"
            type="email"
            placeholder="olivia@example.com"
            withAsterisk
            {...form.getInputProps("email")}
          />

          <Group gap={16}>
            <Button
              type="submit"
              loading={isPending}
              disabled={isPending}
              className="text-primary-button-surface"
              flex={1}
              miw="fit-content"
            >
              Reset password
            </Button>

            <Button
              component={Link}
              href={
                unique_link
                  ? makePath(PAGES.IDENTITY, unique_link, PAGES.SIGNIN)
                  : makePath(PAGES.SIGNIN)
              }
              flex={1}
              miw="fit-content"
              type="button"
              className="bg-primary-text-normal text-primary-text-body"
              disabled={isPending}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      </Box>

      <Divider />

      <Stack gap={4}>
        <Text ta="center" className="text-sm text-primary-text-body">
          Need help? Visit our{" "}
          <Link href="/" className="font-medium text-primary-button-normal">
            Help section{" "}
          </Link>{" "}
          or{" "}
          <Link href="/" className="font-medium text-primary-button-normal">
            contact us
          </Link>
        </Text>

        <Flex
          justify="center"
          className="text-sm text-primary-text-caption divide-x divide-primary-border-light"
        >
          <Link href="/">
            <Text fz={14} px={10}>
              Terms of use
            </Text>
          </Link>
          <Link href="/">
            <Text fz={14} px={10}>
              Privacy policy
            </Text>
          </Link>
          <Link href="/">
            <Text fz={14} px={10}>
              Cookies policy
            </Text>
          </Link>
        </Flex>
      </Stack>
    </Stack>
  );
}
